import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Target, Users, Sparkles, TrendingUp } from 'lucide-react';

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-dark-bg">
      {/* Header */}
      <div className="py-12 px-6 bg-gradient-to-b from-burgundy-darker to-dark-bg">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-cream-gold hover:text-cream-gold-light transition-colors mb-8"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>

          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-cream-gold via-cream-gold-light to-cream-gold bg-clip-text text-transparent mb-6">
            About Evox Media
          </h1>
          <p className="text-xl text-cream-gold-light/80 leading-relaxed">
            Building digital presence for founders and D2C businesses through strategic, brand-aligned content that converts viewers into customers.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto py-16 px-6">
        <div className="space-y-16">
          {/* Our Story */}
          <section>
            <h2 className="text-3xl font-bold text-cream-gold mb-6">Our Story</h2>
            <div className="space-y-4 text-cream-gold-light/80 leading-relaxed">
              <p>
                Evox Media was founded on a simple belief: every founder and D2C business deserves a powerful digital presence that drives real business results. In today's content-saturated world, generic social media strategies don't cut it anymore.
              </p>
              <p>
                We saw too many talented entrepreneurs struggling to build their brand on YouTube and Instagram—wasting time, money, and creative energy on content that didn't align with their vision or business goals. That's why we created a different approach.
              </p>
              <p>
                We don't just create content. We build presence. We craft strategic, brand-aligned channels from the ground up, giving founders and businesses the creative freedom and guaranteed outcomes they need to succeed in the digital landscape.
              </p>
            </div>
          </section>

          {/* What We Do */}
          <section className="bg-gradient-to-br from-dark-card to-burgundy-darker rounded-3xl p-8 md:p-12 border-2 border-burgundy">
            <h2 className="text-3xl font-bold text-cream-gold mb-6">What We Do</h2>
            <div className="space-y-4 text-cream-gold-light/80 leading-relaxed">
              <p>
                Evox Media specializes in building brand-aligned IP channels exclusively from scratch. We handle everything—from strategic content planning and scriptwriting to filming, editing, thumbnail design, and platform optimization.
              </p>
              <p>
                Our end-to-end approach means you get studio-grade content production without the hassle of managing multiple vendors or learning complex platform algorithms. We become an extension of your team, deeply understanding your brand values and business objectives.
              </p>
              <p>
                Whether you're launching your first channel or expanding your digital footprint, we deliver consistent, high-quality content that builds trust, authority, and most importantly—converts your audience into customers.
              </p>
            </div>
          </section>

          {/* Our Values */}
          <section>
            <h2 className="text-3xl font-bold text-cream-gold mb-8">Our Values</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 bg-dark-card rounded-2xl border-2 border-burgundy hover:border-cream-gold transition-colors">
                <div className="w-12 h-12 bg-gradient-to-br from-cream-gold to-cream-gold-dark rounded-xl flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-burgundy-darkest" />
                </div>
                <h3 className="text-xl font-bold text-cream-gold mb-3">Brand Integrity First</h3>
                <p className="text-cream-gold-light/80">
                  We never compromise on brand alignment. Every piece of content we create reflects your values, voice, and vision authentically.
                </p>
              </div>

              <div className="p-6 bg-dark-card rounded-2xl border-2 border-burgundy hover:border-cream-gold transition-colors">
                <div className="w-12 h-12 bg-gradient-to-br from-cream-gold to-cream-gold-dark rounded-xl flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-burgundy-darkest" />
                </div>
                <h3 className="text-xl font-bold text-cream-gold mb-3">Studio-Grade Quality</h3>
                <p className="text-cream-gold-light/80">
                  Professional production values in every frame. We believe great content deserves great execution.
                </p>
              </div>

              <div className="p-6 bg-dark-card rounded-2xl border-2 border-burgundy hover:border-cream-gold transition-colors">
                <div className="w-12 h-12 bg-gradient-to-br from-cream-gold to-cream-gold-dark rounded-xl flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-burgundy-darkest" />
                </div>
                <h3 className="text-xl font-bold text-cream-gold mb-3">Partnership Mindset</h3>
                <p className="text-cream-gold-light/80">
                  We're not just a vendor—we're your dedicated content partner, invested in your long-term success.
                </p>
              </div>

              <div className="p-6 bg-dark-card rounded-2xl border-2 border-burgundy hover:border-cream-gold transition-colors">
                <div className="w-12 h-12 bg-gradient-to-br from-cream-gold to-cream-gold-dark rounded-xl flex items-center justify-center mb-4">
                  <TrendingUp className="w-6 h-6 text-burgundy-darkest" />
                </div>
                <h3 className="text-xl font-bold text-cream-gold mb-3">Results-Driven</h3>
                <p className="text-cream-gold-light/80">
                  We measure success by your business outcomes—engagement, brand authority, and customer conversion.
                </p>
              </div>
            </div>
          </section>

          {/* Who We Serve */}
          <section>
            <h2 className="text-3xl font-bold text-cream-gold mb-6">Who We Serve</h2>
            <div className="space-y-4 text-cream-gold-light/80 leading-relaxed">
              <p>
                We work exclusively with ambitious founders and D2C businesses who understand the power of content marketing and want to build a lasting digital presence. Our clients include:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>E-commerce founders looking to build personal brands</li>
                <li>D2C businesses scaling their customer acquisition</li>
                <li>B2B service providers establishing thought leadership</li>
                <li>Tech entrepreneurs sharing their innovation stories</li>
                <li>Coaches and consultants expanding their reach</li>
              </ul>
              <p className="mt-4">
                If you're ready to invest in building a strategic digital presence that compounds over time, we're ready to partner with you.
              </p>
            </div>
          </section>

          {/* Why From Scratch */}
          <section className="bg-gradient-to-br from-burgundy to-burgundy-dark rounded-3xl p-8 md:p-12 border-2 border-cream-gold/30">
            <h2 className="text-3xl font-bold text-cream-gold mb-6">Why We Start From Scratch</h2>
            <div className="space-y-4 text-cream-gold-light/90 leading-relaxed">
              <p>
                We've made a strategic decision to focus exclusively on building new channels rather than growing existing ones. Here's why:
              </p>
              <p>
                Starting from zero gives us complete creative freedom to implement our proven systems without the constraints of legacy content or inconsistent branding. We can establish the right foundation, voice, and content strategy from day one—ensuring every upload builds towards a cohesive brand narrative.
              </p>
              <p>
                This approach allows us to guarantee outcomes and maintain the highest level of brand integrity. When we build your channel, it's truly yours—aligned with your vision, values, and business objectives from the first video to the thousandth.
              </p>
            </div>
          </section>

          {/* CTA */}
          <section className="text-center py-8">
            <h2 className="text-3xl font-bold text-cream-gold mb-4">Ready to Build Your Presence?</h2>
            <p className="text-xl text-cream-gold-light/80 mb-8">
              Let's discuss how we can transform your brand through strategic content.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:business@evoxmedia.co"
                className="inline-flex items-center justify-center bg-gradient-to-r from-cream-gold to-cream-gold-dark hover:from-cream-gold-dark hover:to-cream-gold text-burgundy-darkest px-8 py-4 text-lg font-bold rounded-full transition-all duration-300 hover:scale-105 hover:shadow-2xl shadow-cream-gold/50"
              >
                Get In Touch
              </a>
              <Link 
                to="/"
                className="inline-flex items-center justify-center border-2 border-cream-gold text-cream-gold hover:bg-cream-gold hover:text-burgundy-darkest px-8 py-4 text-lg font-bold rounded-full transition-all duration-300"
              >
                Back to Home
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
